import { Fragment, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { ProductContext } from "../../context/ProductContext";
import { cartActions } from "../../store/cart-slice";
import { calculatePrice } from "../../utils/discount";
import ImageSlider from "../../components/product/ImageSlider";
import AddToCartAnim from "../../components/animations/AddToCartAnim";
import { useNavigation } from "../../utils/navigation";
import { useTranslation } from "react-i18next";
import descriptions from "../../descriptions.json";

const Container = styled.main`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  background-color: var(--bg-100);

  padding-top: calc(var(--navbar-height) + var(--navbar-mini));
  min-height: 90vh;
  @media (min-width: 768px) {
    padding-top: calc(var(--navbar-height-desktop) + var(--navbar-mini));
  }
`;
const Wrapper = styled.div`
  padding: var(--spacing-xxl) 0;
  width: 94%;

  @media (min-width: 768px) {
    width: 100%;
  }
  @media (min-width: 1025px) {
    max-width: var(--max-width-container);
    display: flex;
    align-items: flex-start;
    position: relative; /* Ovo je potrebno za relativno pozicioniranje unutar roditelja */
    justify-content: flex-end;
  }
`;

const LeftContainer = styled.div`
  @media (min-width: 1025px) {
    width: 700px;
    position: sticky;
    right: calc(
      (100vw - var(--max-width-container)) / 2 + var(--max-width-container) -
        700px
    );
    top: calc(var(--navbar-height-desktop) + var(--font-size-xxlarge) + 40px);
  }
`;

const RightContainer = styled.div`
  @media (min-width: 1025px) {
    width: calc(100% - 720px);
  }
`;

const ProductHeader = styled.div`
  padding-bottom: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  border-bottom: 1px solid var(--bg-300);
`;

const Eyebrow = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-small);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--accent-200);
  background-color: rgba(0, 63, 143, 0.08);
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 999px;
  margin-bottom: var(--spacing-sm);
  text-decoration: none;
  transition: background-color 0.15s ease;
  &:hover {
    background-color: rgba(0, 63, 143, 0.14);
  }
`;
const EyebrowDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: var(--accent-200);
`;

const Title = styled.h1`
  font-size: var(--font-size-large);
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: var(--text-100);
  width: 100%;
  margin: 0 0 var(--spacing-md) 0;
  @media (min-width: 768px) {
    font-size: 2.25rem;
    line-height: 1.2;
  }
`;
const FlexDiv = styled.div`
  display: flex;
  gap: var(--spacing-md);
`;
const FlexDivIcons = styled(FlexDiv)`
  gap: var(--spacing-lg);
  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
    gap: var(--spacing-xl);
  }
`;

const SectionLabel = styled.div`
  font-size: var(--font-size-small);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-200);
  font-weight: 700;
  margin: 0 0 var(--spacing-sm) 0;
  padding-top: var(--spacing-lg);
  border-top: 1px solid var(--bg-300);
`;

const SpecGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin: 0 0 var(--spacing-lg) 0;
`;
const SpecRow = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid var(--bg-300);
`;
const SpecLabel = styled.div`
  font-size: var(--font-size-small);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-200);
  margin-bottom: 2px;
`;
const SpecValue = styled.div`
  font-weight: 600;
  color: var(--text-100);
`;
const CustomLink = styled(Link)`
  font-size: var(--font-size-base);
  font-weight: 600;
`;

const QuantityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 0 var(--spacing-lg) 0;
`;
const QuantityOption = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border: 1px solid
    ${({ $isActive }) => ($isActive ? "var(--primary-200)" : "var(--bg-300)")};
  border-radius: var(--border-radius-base);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    box-shadow 0.15s ease;
  background-color: ${({ $isActive }) =>
    $isActive ? "var(--bg-200)" : "transparent"};
  &:hover {
    border-color: var(--primary-200);
    box-shadow: var(--shadow-medium);
  }
`;
const BestValueTag = styled.span`
  position: absolute;
  top: -9px;
  left: 14px;
  background-color: var(--primary-200);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 999px;
`;
const RadioDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid
    ${({ $isActive }) => ($isActive ? "var(--primary-200)" : "var(--bg-300)")};
  margin-right: 14px;
  flex-shrink: 0;
  &::after {
    content: "";
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: var(--primary-200);
    transform: scale(${({ $isActive }) => ($isActive ? 1 : 0)});
    transition: transform 0.15s ease;
  }
`;
const QuantityLabel = styled.span`
  font-weight: 700;
  font-size: var(--font-size-large);
  color: ${({ $isActive }) =>
    $isActive ? "var(--primary-200)" : "var(--text-100)"};
`;
const QuantityNote = styled.span`
  font-size: var(--font-size-small);
  color: var(--text-200);
  margin-left: 8px;
`;
const QuantityPrice = styled.span`
  font-weight: 700;
  font-size: var(--font-size-large);
  color: ${({ $isActive }) =>
    $isActive ? "var(--primary-200)" : "var(--text-100)"};
`;

const BuyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
  flex-wrap: wrap;
  margin: 0 0 var(--spacing-lg) 0;
`;
const FieldLabel = styled.div`
  font-size: var(--font-size-small);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-200);
  font-weight: 700;
  margin-bottom: 6px;
`;
const AddContainer = styled.div`
  background-color: var(--background-color-hover-alt);
  border-radius: var(--border-radius-large);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xxs);
`;
const AddContainerButton = styled.button`
  background-color: var(--bg-200);
  border: none;
  padding: 0;
  width: 30px;
  height: 30px;
  border-radius: var(--border-radius-large);
  color: var(--text-100);
  &:hover {
    background-color: var(--bg-200);
  }
`;
const Input = styled.input`
  width: 40px;
  height: 30px;
  padding: 0 var(--spacing-sm);
  margin: 0 var(--spacing-sm);
  background-color: var(--bg-200);
  text-align: center;
  font-size: var(--font-size-base);
  border-radius: var(--border-radius-small);
  font-weight: 600;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  @media (min-width: 768px) {
    width: 80px;
  }
  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const ButtonWrapper = styled.div`
  position: relative; /* Roditeljski kontejner koji omogućava apsolutno pozicioniranje za decu */
  display: inline-block; /* Omogućava kontejneru da se prilagodi veličini sadržaja */
  width: 100%;
  text-align: center;
`;

const Button = styled.button`
  width: 100%;
  border-radius: 0;
  background-color: var(--primary-200);
  color: var(--bg-200);

  &:hover {
    background-color: var(--bg-200);
    color: var(--primary-100);
  }
  &:disabled {
    background-color: var(--bg-300);
    border-color: var(--bg-300);
    color: var(--text-100);
    cursor: not-allowed;
  }
`;

const IconDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;
const SVGIcon = styled.svg`
  background-color: rgba(86, 147, 191, 0.1);
  padding: 4px 8px;
  border-radius: 50%;
`;
const IconText = styled.span`
  font-size: var(--font-size-small);
  color: var(--p-color);
`;

const DescText = styled.p`
  font-weight: 200;
`;

const RadioContainer = styled.div`
  display: flex;
  gap: var(--spacing-xl);
  border-bottom: 1px solid var(--bg-300);
  width: 94%;
  margin-bottom: 20px;
  @media (min-width: 1025px) {
    max-width: var(--max-width-container);
  }
`;

// Styled button for section selection
const RadioButton = styled.button`
  background: none;
  border: none;
  padding: 10px 0;
  cursor: pointer;
  font-size: var(--font-size-base);
  font-weight: 600;
  color: ${({ isSelected }) =>
    isSelected ? "var(--accent-200)" : "var(--text-200)"};
  border-bottom: 2px solid
    ${({ isSelected }) => (isSelected ? "var(--accent-200)" : "transparent")};
  margin-bottom: -1px;
  transition: color 0.15s ease;
  &:hover {
    color: var(--accent-200);
  }
`;

// Container for the content sections
const SectionContainer = styled.div`
  width: 94%;
  background-color: var(--bg-200);
  padding: 14px;
  border-radius: var(--border-radius-base);

  @media (min-width: 768px) {
    display: flex;
    position: relative; /* Ovo je potrebno za relativno pozicioniranje unutar roditelja */
    justify-content: flex-end;
    flex-direction: column;
  }
  @media (min-width: 1025px) {
    max-width: var(--max-width-container);
  }
`;
const SubTitle = styled.h2`
  font-size: 20px;
  margin: 20px 0 10px;
  color: var(--text-200);
`;
const Paragraph = styled.p`
  margin-bottom: 16px;
`;

const ProductMain = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const { goToCategory, goToShop } = useNavigation();
  const [selectedQuantity, setSelectedQuantity] = useState(10);
  const [selectedSection, setSelectedSection] = useState("description");
  const dispatch = useDispatch();
  const {
    setIsCartOpen,
    maxAttributes,
    loadProductBySlug,
    product,
    setProduct,
    currencyTag,
    loadRecommededProductsBySlug,
  } = useContext(ProductContext);
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [savingsPercentage, setSavingsPercentage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!slug) {
      return;
    }
    setProduct(null);
    const fetchProduct = async () => {
      try {
        const productData = await loadProductBySlug(slug); // Pretpostavljamo da loadProductBySlug vraća podatke o proizvodu
        setProduct(productData);
        window.scrollTo(0, 0);
      } catch (error) {
        console.error("Failed to load product:", error);
        // Ovde možete postaviti neki state za grešku ako želite da obavestite korisnika
      }
    };

    fetchProduct();
    loadRecommededProductsBySlug(slug);
  }, [slug, i18n.language]);
  useEffect(() => {
    if (product) {
      const newDiscountedPrice = calculatePrice(
        product.price,
        selectedQuantity
      );
      setDiscountedPrice(newDiscountedPrice);

      const newSavingsPercentage = Math.round(
        ((product.price - newDiscountedPrice) / product.price) * 100
      );
      setSavingsPercentage(newSavingsPercentage);
    }
  }, [product, selectedQuantity]);

  const addToCart = (selectedQuantity) => {
    dispatch(
      cartActions.addToCart({
        product: product,
        quantity: selectedQuantity,
      })
    );
    setIsCartOpen(true);
    setIsAnimating(true);
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
  };
  const getDiscountData = (quantity) => {
    const discountedPrice = calculatePrice(product.price, quantity);
    const savings = Math.round(
      ((product.price - discountedPrice) / product.price) * 100
    );
    return { discountedPrice, savings };
  };
  const handleChange = (event) => {
    const value = Math.max(1, Math.min(999, Number(event.target.value)));
    setSelectedQuantity(value);
  };

  const [descData, setDescData] = useState(null);

  useEffect(() => {
    const item = descriptions.find((d) => d.slug === slug);
    setDescData(item ?? null);
  }, [slug, descriptions]);

  if (!descData) {
    return <SectionContainer>{t("loading")}</SectionContainer>;
  }

  // Odredi jezik i ključeve
  const lang = i18n.language.startsWith("sr")
    ? "sr"
    : i18n.language.startsWith("de")
    ? "de"
    : "en";
  const shortKey = `short_${lang}`;
  const longKey = `long_${lang}_html`;

  const paras = descData[longKey] || descData[`long_${lang}`] || [];

  // Niz opisa nema fiksan broj elemenata (nekad 6, nekad 7+ - zavisi od
  // broja sekcija koje je generator napisao za dati proizvod), pa fiksna
  // pozicija [title1, introPara, title2, ...] pogresno mapira sadrzaj kad
  // ima vise/manje elemenata (npr. "How to Use: ..." zavrsi kao naslov, a
  // poslednja sekcija se tiho izgubi). Grupisemo po heuristici umesto toga.
  // Sudi po vidljivom tekstu (bez HTML tagova) - inace kratak naslov sa
  // ubačenim <a href="..."> linkom (npr. "Informationen zur Marke <a ...>")
  // ispadne duži od 60 karaktera zbog markupa i pogrešno se tretira kao
  // sadržaj prethodne sekcije umesto kao novi naslov.
  const isDescTitle = (str) => {
    const plain = str.replace(/<[^>]*>/g, "");
    return (
      plain.length < 60 &&
      (plain.match(/\./g) || []).length < 2 &&
      !/^[A-Za-z][\w /-]*:\s/.test(plain)
    );
  };

  const descSections = [];
  {
    let current = null;
    paras.forEach((raw) => {
      const text = (raw || "").replace(/[\r\n]+/g, " ").trim();
      if (!text) return;
      if (isDescTitle(text)) {
        current = { title: text, contents: [] };
        descSections.push(current);
      } else if (current) {
        current.contents.push(text);
      } else {
        // Tekst pre prvog prepoznatog naslova - retko, ali ne bacaj ga.
        current = { title: "", contents: [text] };
        descSections.push(current);
      }
    });
  }

  const stockLabel = product?.is_in_stock
    ? product.is_in_stock
        .split("_")
        .map((word) => word[0].toUpperCase() + word.slice(1))
        .join(" ")
    : "";
  const stockColor =
    product?.is_in_stock === "in_stock"
      ? "#2e7d32"
      : product?.is_in_stock === "on_request"
      ? "#b8860b"
      : "#c62828";

  return (
    <Container>
      {product && (
        <>
          <Wrapper>
            <LeftContainer>
              <div style={{ position: "relative" }}>
                <ImageSlider images={product.images} productName={product.name} />
              </div>
            </LeftContainer>
            <RightContainer>
              <ProductHeader>
                <Eyebrow to={goToCategory(product.category_name)}>
                  <EyebrowDot />
                  {product.category_name}
                </Eyebrow>
                <Title>
                  {product.name} {product.format} (
                  {product.pouches_per_can} {t("PRODUCT.POUCHES_PER_CAN")}){" "}
                  {product.nicotine}MG {t("PRODUCT.NICOTINE")}
                </Title>
                <DescText>{descData[shortKey]}</DescText>
              </ProductHeader>

              <QuantityList>
                {[1, 10, 50].map((quantity, index) => {
                  const { discountedPrice, savings } =
                    getDiscountData(quantity);
                  const isActive = selectedQuantity === quantity;

                  return (
                    <QuantityOption
                      key={index}
                      $isActive={isActive}
                      onClick={() => setSelectedQuantity(quantity)}
                    >
                      {quantity === 50 && (
                        <BestValueTag>{t("PRODUCT.BEST_VALUE")}</BestValueTag>
                      )}
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <RadioDot $isActive={isActive} />
                        <div>
                          <QuantityLabel $isActive={isActive}>
                            {quantity}x
                          </QuantityLabel>
                          {quantity !== 1 && (
                            <QuantityNote>
                              -{savings}% · {currencyTag}
                              {discountedPrice}/piece
                            </QuantityNote>
                          )}
                        </div>
                      </div>
                      <QuantityPrice $isActive={isActive}>
                        {currencyTag}
                        {(product.price * quantity).toFixed(2)}
                      </QuantityPrice>
                    </QuantityOption>
                  );
                })}
              </QuantityList>

              <BuyRow>
                <div>
                  <FieldLabel>{t("PRODUCT.QUANTITY")}</FieldLabel>
                  <AddContainer>
                    <AddContainerButton
                      onClick={() => {
                        if (selectedQuantity > 1) {
                          setSelectedQuantity(selectedQuantity - 1);
                        }
                      }}
                      aria-label={t("ARIA.DECREASE_QUANTITY")}
                    >
                      <svg
                        viewBox="0 0 160 161"
                        fill="none"
                        className="svg-icon"
                        width="26px"
                        height="26px"
                      >
                        <path
                          d="M79.516 159.561c-43.324 0-78.57-35.467-78.57-79.061 0-43.594 35.246-79.061 78.57-79.061 43.323 0 78.57 35.467 78.57 79.061 0 43.594-35.247 79.061-78.57 79.061zm0-148.661c-38.14 0-69.168 31.222-69.168 69.6 0 38.378 31.028 69.6 69.168 69.6 38.14 0 69.168-31.222 69.168-69.6 0-38.378-31.028-69.6-69.168-69.6z"
                          fill="none"
                        ></path>
                        <path
                          d="M108.69 74.379H49.407c-3.359 0-6.083 2.74-6.083 6.12 0 3.382 2.724 6.122 6.084 6.122h59.282c3.36 0 6.084-2.74 6.084-6.121 0-3.38-2.724-6.121-6.084-6.121z"
                          fill="var(--text-200)"
                        ></path>
                      </svg>
                    </AddContainerButton>
                    <div>
                      <Input
                        type="number"
                        max="999"
                        min="0"
                        value={selectedQuantity}
                        onChange={handleChange}
                        aria-label={t("PRODUCT.QUANTITY")}
                      />
                    </div>

                    <AddContainerButton
                      onClick={() => setSelectedQuantity(selectedQuantity + 1)}
                      aria-label={t("ARIA.INCREASE_QUANTITY")}
                    >
                      <svg
                        viewBox="0 0 161 161"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="svg-icon"
                        width="26px"
                        height="26px"
                      >
                        <path
                          d="M119.668 74.283H85.76V40.375a6.217 6.217 0 00-12.434 0v33.908H39.418a6.217 6.217 0 000 12.434h33.908v33.908a6.217 6.217 0 1012.434 0V86.717h33.908a6.217 6.217 0 100-12.434z"
                          fill="var(--text-200)"
                        ></path>
                        <path
                          d="M80.013 159.561C36.419 159.561.952 124.094.952 80.5c0-43.594 35.467-79.061 79.06-79.061 43.595 0 79.062 35.467 79.062 79.061 0 43.594-35.467 79.061-79.061 79.061zm0-148.661c-38.378 0-69.6 31.222-69.6 69.6 0 38.378 31.222 69.6 69.6 69.6 38.378 0 69.6-31.222 69.6-69.6 0-38.378-31.222-69.6-69.6-69.6z"
                          fill="none"
                        ></path>
                      </svg>
                    </AddContainerButton>
                  </AddContainer>
                </div>
                <div style={{ textAlign: "right" }}>
                  <FieldLabel>{t("PRODUCT.TOTAL_PRICE")}</FieldLabel>
                  <QuantityPrice $isActive style={{ fontSize: "var(--font-size-xlarge)" }}>
                    {currencyTag}
                    {(product.price * selectedQuantity).toFixed(2)}
                  </QuantityPrice>
                </div>
              </BuyRow>

              <ButtonWrapper>
                <Button
                  onClick={() => addToCart(selectedQuantity)}
                  disabled={product.is_in_stock === "out_of_stock"}
                >
                  {product.is_in_stock !== "out_of_stock"
                    ? t("BUTTONS.ADD_TO_CART")
                    : t("BUTTONS.AVAILABLE_SOON") + "!"}
                </Button>

                {isAnimating && (
                  <AddToCartAnim
                    isAnimating={isAnimating}
                    onComplete={handleAnimationComplete}
                  />
                )}
              </ButtonWrapper>

              <FlexDivIcons style={{ margin: "var(--spacing-lg) 0" }}>
                <IconDiv>
                  <SVGIcon
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.7667 2.50445L14.1843 2.84068H10.3906C10.1835 2.84068 10.0156 3.00859 10.0156 3.21568C10.0156 3.42278 10.1835 3.59068 10.3906 3.59068H12.8853L12.1697 4.00379L20.5891 8.86478V12.4667C20.5891 12.6107 20.5178 12.7343 20.3931 12.8063L18.4415 13.933C18.3792 13.969 18.3078 13.969 18.2455 13.933C18.1831 13.897 18.1475 13.8352 18.1475 13.7633V10.2744L9.72816 5.41342L7.34442 6.78967C7.30214 6.77312 7.25616 6.76407 7.20802 6.76407H0.796922C0.589828 6.76407 0.421922 6.93198 0.421922 7.13907C0.421922 7.34617 0.589828 7.51407 0.796922 7.51407H6.74278C6.74044 7.53957 6.73922 7.5655 6.73922 7.59175V11.6251H3.02902C2.82192 11.6251 2.65402 11.793 2.65402 12.0001C2.65402 12.2072 2.82192 12.3751 3.02902 12.3751H6.73922V16.0172H0.796875C0.589781 16.0172 0.421875 16.1852 0.421875 16.3922C0.421875 16.5993 0.589781 16.7672 0.796875 16.7672H6.82294C6.88955 16.8982 6.99431 17.0083 7.13123 17.0874L14.7667 21.4957C15.016 21.6397 15.3014 21.6397 15.5507 21.4957L23.1861 17.0874C23.4354 16.9435 23.5781 16.6963 23.5781 16.4084V7.59175C23.5781 7.30389 23.4354 7.05671 23.1861 6.91276L15.5506 2.50445C15.3014 2.3605 15.016 2.3605 14.7667 2.50445ZM7.57317 2.84064C7.78027 2.84064 7.94817 3.00854 7.94817 3.21564C7.94817 3.42273 7.78027 3.59064 7.57317 3.59064H2.56031C2.35322 3.59064 2.18531 3.42273 2.18531 3.21564C2.18531 3.00854 2.35322 2.84064 2.56031 2.84064H7.57317Z"
                      fill="var(--accent-200)"
                    />
                  </SVGIcon>
                  <IconText>{t("PRODUCT.FAST_DELIVERY")}</IconText>
                </IconDiv>
                <IconDiv>
                  <SVGIcon
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M22.6985 14.4272H23.4375V12.1919C23.4375 12.0767 23.4173 11.9782 23.3717 11.8708L21.8297 8.2401C21.6816 7.89135 21.3323 7.67291 20.9226 7.67291H17.3768V17.2668H17.7274C17.7274 15.9941 18.7595 14.962 20.0321 14.962C21.3048 14.962 22.3369 15.9942 22.3369 17.2668H22.8544C23.1755 17.2668 23.4374 17.0049 23.4374 16.6837V15.8832H22.6984C22.5504 15.8832 22.4296 15.7625 22.4296 15.6144V14.6959C22.4297 14.5479 22.5504 14.4272 22.6985 14.4272ZM17.8218 11.9016V8.86789H20.7981L22.0865 11.9016H17.8218ZM15.8495 5.17822C11.4575 5.17822 7.06552 5.17822 2.67356 5.17822C2.46647 5.17822 2.29856 5.34613 2.29856 5.55322C2.29856 5.76032 2.46647 5.92822 2.67356 5.92822H4.75181C5.11598 5.92822 5.41392 6.22616 5.41392 6.59033C5.41392 6.9545 5.11598 7.25244 4.75181 7.25244H1.4393H0.9375C0.730406 7.25244 0.5625 7.42035 0.5625 7.62744C0.5625 7.83453 0.730406 8.00244 0.9375 8.00244H4.15823C4.52241 8.00244 4.82034 8.30038 4.82034 8.66455C4.82034 9.02872 4.52241 9.32666 4.15823 9.32666H1.99303C1.78594 9.32666 1.61803 9.49457 1.61803 9.70166C1.61803 9.90875 1.78594 10.0767 1.99303 10.0767H5.10694C5.47111 10.0767 5.76905 10.3746 5.76905 10.7388C5.76905 11.1029 5.47111 11.4009 5.10694 11.4009C3.71714 11.4009 2.3273 11.4008 0.9375 11.4009C0.730406 11.4009 0.5625 11.5688 0.5625 11.7759C0.5625 11.983 0.730406 12.1509 0.9375 12.1509H4.15823V16.4895C4.15823 16.9176 4.50745 17.2669 4.93561 17.2669H6.54398C6.54398 15.9942 7.57612 14.9621 8.84873 14.9621C10.1214 14.9621 11.1535 15.9942 11.1535 17.2669H16.6268V5.9556C16.6268 5.52744 16.2776 5.17822 15.8495 5.17822ZM10.7812 14.2861C9.00075 14.2861 7.55742 12.8428 7.55742 11.0623C7.55742 9.28189 9.00075 7.83857 10.7812 7.83857C12.5617 7.83857 14.005 9.28189 14.005 11.0623C14.005 12.8428 12.5617 14.2861 10.7812 14.2861ZM10.3578 11.3467L11.9253 9.76349C12.1109 9.57608 12.4136 9.57557 12.6005 9.76138C12.7873 9.94714 12.7879 10.249 12.6025 10.4361C11.9669 11.0776 11.3355 11.7234 10.6969 12.3621C10.5102 12.5488 10.2074 12.5488 10.0207 12.3621L8.96124 11.3026C8.77453 11.1159 8.77453 10.8131 8.96124 10.6264C9.14794 10.4397 9.45075 10.4397 9.63745 10.6264L10.3578 11.3467ZM8.84878 15.712C7.99008 15.712 7.29403 16.4081 7.29403 17.2668C7.29403 18.1255 7.99013 18.8216 8.84878 18.8216C9.70748 18.8216 10.4036 18.1255 10.4036 17.2668C10.4036 16.4081 9.70748 15.712 8.84878 15.712ZM8.84878 17.7965C8.556 17.7965 8.31905 17.5596 8.31905 17.2668C8.31905 16.974 8.556 16.7371 8.84878 16.7371C9.14156 16.7371 9.37852 16.974 9.37852 17.2668C9.37852 17.5596 9.14156 17.7965 8.84878 17.7965ZM20.0322 15.712C19.1735 15.712 18.4774 16.4081 18.4774 17.2668C18.4774 18.1255 19.1735 18.8216 20.0322 18.8216C20.8909 18.8216 21.5869 18.1255 21.5869 17.2668C21.587 16.4081 20.8909 15.712 20.0322 15.712ZM20.0322 17.7965C19.7394 17.7965 19.5024 17.5596 19.5024 17.2668C19.5024 16.974 19.7394 16.7371 20.0322 16.7371C20.325 16.7371 20.5619 16.974 20.5619 17.2668C20.5619 17.5596 20.325 17.7965 20.0322 17.7965Z"
                      fill="var(--accent-200)"
                    />
                  </SVGIcon>
                  <IconText>{t("PRODUCT.EXPRESS_DELIVERY")}</IconText>
                </IconDiv>
              </FlexDivIcons>

              <SectionLabel>{t("PRODUCT.SPECIFICATION")}</SectionLabel>
              <SpecGrid>
                <SpecRow>
                  <SpecLabel>{t("PRODUCT.FLAVOR")}</SpecLabel>
                  <SpecValue>{product.flavor}</SpecValue>
                </SpecRow>
                <SpecRow>
                  <SpecLabel>{t("PRODUCT.FORMAT")}</SpecLabel>
                  <SpecValue>{product.format}</SpecValue>
                </SpecRow>
                <SpecRow>
                  <SpecLabel>{t("PRODUCT.POUCHES_PER_CAN")}</SpecLabel>
                  <SpecValue>{product.pouches_per_can}</SpecValue>
                </SpecRow>
                <SpecRow>
                  <SpecLabel>{t("PRODUCT.NET_WEIGHT")}</SpecLabel>
                  <SpecValue>{product.net_weight}g</SpecValue>
                </SpecRow>
                <SpecRow>
                  <SpecLabel>{t("PRODUCT.NICOTINE")}</SpecLabel>
                  <SpecValue>{product.nicotine}MG</SpecValue>
                </SpecRow>
                {product.manufacturer && (
                  <SpecRow>
                    <SpecLabel>{t("PRODUCT.MANUFACTURER")}</SpecLabel>
                    <SpecValue>{product.manufacturer}</SpecValue>
                  </SpecRow>
                )}
                <SpecRow>
                  <SpecLabel>{t("PRODUCT.STOCK")}</SpecLabel>
                  <SpecValue style={{ color: stockColor }}>
                    {stockLabel}
                  </SpecValue>
                </SpecRow>
                <SpecRow>
                  <SpecLabel>{t("PRODUCT.TYPE")}</SpecLabel>
                  <SpecValue>
                    <CustomLink to={goToShop()}>Nicotine pouches</CustomLink>
                  </SpecValue>
                </SpecRow>
              </SpecGrid>
            </RightContainer>
          </Wrapper>

          <RadioContainer>
            <RadioButton
              isSelected={selectedSection === "description"}
              onClick={() => setSelectedSection("description")}
            >
              {t("PRODUCT.DESCRIPTION")}
            </RadioButton>
            <RadioButton
              isSelected={selectedSection === "section2"}
              onClick={() => setSelectedSection("section2")}
            >
              {t("PRODUCT.RATINGS")}
            </RadioButton>
          </RadioContainer>

          <SectionContainer>
            <FlexDiv>
              <SectionContainer>
                {selectedSection === "description" ? (
                  <>
                    {descSections.map(({ title, contents }, idx) =>
                      title || contents.length ? (
                        <Fragment key={idx}>
                          {title &&
                            (idx === 0 ? (
                              <Title dangerouslySetInnerHTML={{ __html: title }} />
                            ) : (
                              <SubTitle dangerouslySetInnerHTML={{ __html: title }} />
                            ))}
                          {contents.map((html, i) => (
                            <Paragraph
                              key={i}
                              dangerouslySetInnerHTML={{ __html: html }}
                            />
                          ))}
                        </Fragment>
                      ) : null
                    )}
                  </>
                ) : (
                  <Paragraph>{t("PRODUCT.NO_INFO")}</Paragraph>
                )}
              </SectionContainer>
            </FlexDiv>
          </SectionContainer>
        </>
      )}
    </Container>
  );
};

export default ProductMain;
